import React, { useEffect, useMemo, useState } from 'react';
import { Table, DatePicker, Input, Select, Button, Space, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
    GET_PORTAL_ORDERS,
    GET_PORTAL_PICKUP_HUB_OPTIONS,
    GET_PORTAL_EXTERNAL_STATUSES,
} from 'graphql/queries/order';
import {
    PagedPortalOrder,
    PortalOrderSummary,
    PortalPickupHubOption,
    PortalExternalStatusOption,
} from 'graphql/types';
import { EXTERNAL_ORDER_STATUS_CODES, getExternalStatusLabel } from 'utils/orderStatus';
import { formatUtcToLocal } from 'utils/dateTime';
import { EXPORT_PERMISSION } from 'utils/orderExport';
import { usePermission } from 'components/PermissionProvider';
import { useOrderSelection } from 'components/OrderSelectionContext';
import OrderExportToolbar from 'components/OrderExportToolbar';
import OrderTotalLabel from 'components/OrderTotalLabel';

const { RangePicker } = DatePicker;

interface OrderFilters {
    trackingNumber: string;
    channelOrderNumber: string;
    externalTrackingNumber: string;
    /** 包裹状态，多选；空数组表示不限 */
    statuses: string[];
    dateRange: [Dayjs | null, Dayjs | null];
    /** 揽收口岸（外部口岸标识符），多选；空数组表示不限 */
    pickupHubCodes: string[];
}

const emptyFilters: OrderFilters = {
    trackingNumber: '',
    channelOrderNumber: '',
    externalTrackingNumber: '',
    statuses: [],
    dateRange: [null, null],
    pickupHubCodes: [],
};

/*
 * 筛选项网格的列宽下限，单位 px。
 *
 * 筛选区不再用「定宽 + flex 换行」：本页嵌在 PortalLayout 的两级侧边栏内（72~132px 图标栏 +
 * 180~280px 菜单栏 + 两层 24px 内边距），1280px 视口下可用宽度只剩约 772~932px，
 * 5 个定宽筛选项加按钮无论怎么调都排不进一行，硬调宽度只是把溢出推给下一次反馈。
 *
 * 改用 auto-fill 网格后各项宽度 = 列宽：窄屏自动减少列数并换行，宽屏则把富余宽度平分给各列，
 * 屏幕越大订单号显示得越完整。下限取 280px 是因为客户订单号普遍 20~28 位，
 * 14px 字号下 280px 约可完整显示 28 个字符（已扣除内边距与 allowClear 图标）。
 */
const FILTER_MIN_COLUMN_WIDTH = 280;

/**
 * 带标签的筛选项容器：标签在上、控件在下，与设计稿一致。
 *
 * <p>标签排版对齐 antd Table 表头（14px / font-weight 600 / rgba(0,0,0,0.88)）：
 * 筛选区与下方列表是同一套字段名，标签若用更小更浅的字号，同名字段在一屏内呈现两种权重，
 * 读起来像两个不相干的区域。
 */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex flex-col gap-1">
        {/* 颜色走内联 style 而非 Tailwind 任意值：带逗号的 rgba() 类名在构建产物里未被生成 */}
        <span className="text-sm font-semibold" style={{ color: 'rgba(0, 0, 0, 0.88)' }}>
            {label}
        </span>
        {children}
    </div>
);

/**
 * 下发给服务端的本地时刻格式：不带时区，秒恒为 00。
 *
 * <p>不给 to 补 59 秒：服务端会把 to 对齐到该分钟的 59.999，这里补了反而与「用户所选即所传」不一致。
 *
 * <p>分隔符 T 用 [] 转义：dayjs 只替换已知 token，裸写的 T 眼下也能原样输出，但一旦哪天它成了
 * token，这里会静默产出一个服务端解不开的串。
 */
const fmtDateTime = (d: Dayjs | null | undefined): string | undefined => d?.format('YYYY-MM-DD[T]HH:mm:ss');

/**
 * 选择器里「只点日期、不点时间」时补的默认时分：起始 00:00、结束 23:59。
 *
 * <p>必须用 startOf/endOf 构造，<b>不能写 dayjs('00:00', 'HH:mm')</b>——本项目没有注册
 * customParseFormat 插件，dayjs 会忽略第二个格式参数、按 new Date('00:00') 解析，得到 Invalid Date，
 * 选完日期后输入框直接显示 "Invalid Date"。
 *
 * <p>日期部分（构造时的今天）不参与展示，antd 只从中取时分，所以放模块级常量、跨天也不会出错。
 *
 * <p>导出仅为便于单元测试锁住这个值。
 */
export const DEFAULT_TIME_OF_DAY: [Dayjs, Dayjs] = [dayjs().startOf('day'), dayjs().endOf('day').startOf('minute')];

/** 下单时间区间的校验结果：通过时为 null，否则为待提示的 i18n key */
export type DateRangeError =
    | 'orderQuery.filters.dateRangeIncomplete'
    | 'orderQuery.filters.dateRangeReversed'
    | null;

/**
 * 校验下单时间区间，与服务端 OrderQueryServiceImpl.resolveOrderTimeRange 同一套规则：
 * 要么两端都不选，要么两端都选且起始不晚于终止。
 *
 * <p>只选一端会被下游 order-service 静默忽略，用户拿到一份「以为筛过、实际没筛」的结果，
 * 所以在发请求前就拦住并提示。
 *
 * <p>比较粒度是分钟：两端选同一分钟是合法的最小区间（该分钟整体命中），不能判成反向。跨度不设上限，
 * 与服务端一致。
 *
 * <p>导出仅为便于单元测试直接锁住这套规则（组件交互无法构造出「只选一端」的状态）。
 *
 * @param dateRange 页面上的时间区间
 * @returns 校验不通过时返回提示文案的 i18n key，通过时返回 null
 */
export const validateDateRange = (dateRange: [Dayjs | null, Dayjs | null]): DateRangeError => {
    const [from, to] = dateRange;
    if (!from && !to) return null;
    if (!from || !to) return 'orderQuery.filters.dateRangeIncomplete';
    if (from.isAfter(to, 'minute')) return 'orderQuery.filters.dateRangeReversed';
    return null;
};

/**
 * 把页面筛选状态转换为后端 PortalOrderFilter 入参
 *
 * <p>空串 / 空数组统一转成 undefined，避免把「不限」当成过滤条件下发。
 *
 * <p>下单时间成对下发：调用方（handleSearch / 缓存恢复）已用 validateDateRange 保证两端齐全，
 * 这里的 from && to 只是防御，避免任何遗漏路径把残缺区间发到服务端换来一个参数错误。
 *
 * <p>字段名为 orderTimeRange：筛的是订单 order_time（业务口径的下单时间），与列表展示的
 * 「包裹创建时间」同一字段，也与客服平台包裹列表页的「订单时间」口径一致。
 */
const buildQueryFilter = (f: OrderFilters) => {
    const from = fmtDateTime(f.dateRange[0]);
    const to = fmtDateTime(f.dateRange[1]);
    return {
        trackingNumber: f.trackingNumber || undefined,
        channelOrderNumber: f.channelOrderNumber || undefined,
        externalTrackingNumber: f.externalTrackingNumber || undefined,
        externalStatuses: f.statuses.length > 0 ? f.statuses : undefined,
        orderTimeRange: from && to ? { from, to } : undefined,
        pickupHubCodes: f.pickupHubCodes.length > 0 ? f.pickupHubCodes : undefined,
    };
};

/* 会话级缓存 key：进详情页再返回时用它还原筛选条件与分页，关掉标签页即失效 */
const QUERY_STATE_KEY = 'orderQuery.state';

/** 缓存中的筛选状态；Dayjs 无法 JSON 序列化，时刻以 YYYY-MM-DDTHH:mm:ss 字符串存取 */
interface CachedQueryState {
    filters: Omit<OrderFilters, 'dateRange'> & { dateRange: [string | null, string | null] };
    page: number;
    pageSize: number;
}

/** 取缓存里的字符串数组字段；类型不符（缓存被篡改）时退回空数组 */
const readStringArray = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** 页面查询状态的初始值，来自会话缓存，无缓存或缓存损坏时回退到空条件第一页 */
const loadQueryState = (): { filters: OrderFilters; page: number; pageSize: number } => {
    const fallback = { filters: emptyFilters, page: 1, pageSize: 20 };
    try {
        const raw = sessionStorage.getItem(QUERY_STATE_KEY);
        if (!raw) return fallback;
        const cached = JSON.parse(raw) as CachedQueryState;
        const [from, to] = cached.filters.dateRange || [null, null];
        const dateRange: OrderFilters['dateRange'] = [from ? dayjs(from) : null, to ? dayjs(to) : null];
        return {
            filters: {
                ...emptyFilters,
                ...cached.filters,
                /*
                 * 恢复的条件会直接触发查询、绕过「搜索」按钮上的校验，
                 * 所以这里把不合规的区间（缓存结构不符或被手工篡改）整体丢弃。
                 */
                dateRange: validateDateRange(dateRange) === null ? dateRange : [null, null],
                /*
                 * 两个多选字段必须校验类型：本函数的 try/catch 只兜得住 JSON.parse，
                 * 而这两个值会在 render 与 useEffect 里被 .filter/.length 消费——
                 * 缓存被篡改成非数组时那里抛错，catch 覆盖不到，页面直接白屏。
                 */
                statuses: readStringArray(cached.filters?.statuses),
                pickupHubCodes: readStringArray(cached.filters?.pickupHubCodes),
            },
            page: cached.page || 1,
            pageSize: cached.pageSize || 20,
        };
    } catch {
        /* 缓存结构过期或被手工篡改时直接忽略，不能因此让页面打不开 */
        return fallback;
    }
};

const saveQueryState = (filters: OrderFilters, page: number, pageSize: number) => {
    const state: CachedQueryState = {
        filters: {
            ...filters,
            dateRange: [fmtDateTime(filters.dateRange[0]) ?? null, fmtDateTime(filters.dateRange[1]) ?? null],
        },
        page,
        pageSize,
    };
    try {
        sessionStorage.setItem(QUERY_STATE_KEY, JSON.stringify(state));
    } catch {
        /* 隐私模式等场景下 sessionStorage 可能不可写，仅牺牲「返回保留条件」，不影响查询本身 */
    }
};

const OrderQueryPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    /* 惰性读一次会话缓存，供下面各 state 初始化复用 */
    const [initialState] = useState(loadQueryState);

    /* 编辑态：用户正在改的筛选条件，改动不触发查询 */
    const [filters, setFilters] = useState<OrderFilters>(initialState.filters);
    /* 提交态：只有点「搜索」/「重置」才同步，useQuery 只依赖它 */
    const [appliedFilters, setAppliedFilters] = useState<OrderFilters>(initialState.filters);
    const [page, setPage] = useState(initialState.page);
    const [pageSize, setPageSize] = useState(initialState.pageSize);

    /* 已提交的条件与分页写入会话缓存，使「进详情页 → 返回」不丢失查询上下文 */
    useEffect(() => {
        saveQueryState(appliedFilters, page, pageSize);
    }, [appliedFilters, page, pageSize]);

    /* 揽收口岸下拉（外部口岸）；列表口岸列直接读后端返回的 pickupHubName，无需前端做映射 */
    const { data: hubData } = useQuery<{ portalPickupHubOptions: PortalPickupHubOption[] }>(
        GET_PORTAL_PICKUP_HUB_OPTIONS
    );
    const { data: statusData } = useQuery<{ portalExternalStatuses: PortalExternalStatusOption[] }>(
        GET_PORTAL_EXTERNAL_STATUSES
    );

    /*
     * 下拉返回后剔除筛选条件里已不存在的口岸（会话缓存恢复的旧条件、运维下线或改名的口岸）。
     * 服务端会把这类 code 判定为非法入参直接报错，而条件又被会话缓存持久化——不在这里剔除，
     * 用户刷新页面恢复的还是同一个条件、报同一个错，只能点「重置」才能脱困。
     * options 为空时不动：那更可能是口岸服务暂时不可用，此刻清空等于把用户选好的条件静默丢掉。
     *
     * 这里刻意不给用户任何提示，也不为此推迟订单查询，代价是极罕见情况下会先弹一次
     * 「所选揽收口岸已不可用」再自愈、且剔空后列表静默变为不限口岸。之所以能接受：
     * common_config 的口岸配置在业务上只增不减（新开口岸才改配置，几乎不下线、不改 code），
     * 本分支实际是兜底而非常态路径。若哪天真出现口岸下线/改名的运维动作，这个取舍要重新评估——
     * 届时应改为「口岸条件非空且 hubData 未返回时 skip 订单查询」并在剔除后给一条 message 提示。
     */
    useEffect(() => {
        const validCodes = (hubData?.portalPickupHubOptions || [])
            .map((h) => h.externalPortCode)
            .filter((code): code is string => !!code);
        if (validCodes.length === 0) return;
        const dropUnknown = (prev: OrderFilters) => {
            const kept = prev.pickupHubCodes.filter((code) => validCodes.includes(code));
            return kept.length === prev.pickupHubCodes.length ? prev : { ...prev, pickupHubCodes: kept };
        };
        setFilters(dropUnknown);
        setAppliedFilters(dropUnknown);
    }, [hubData]);

    const appliedQueryFilter = useMemo(() => buildQueryFilter(appliedFilters), [appliedFilters]);

    const { data, loading, refetch } = useQuery<{ portalOrders: PagedPortalOrder }>(GET_PORTAL_ORDERS, {
        variables: {
            filter: appliedQueryFilter,
            page: page - 1,
            size: pageSize,
        },
    });

    /*
     * 深翻页保护下的可翻页数由服务端下发（上限值是服务端配置，前端不自行计算，否则调整后两边会脱节）。
     *
     * 未拿到数据时按 0 处理，即「暂不截断」。服务端下发 0 只会是「查询结果为空」，
     * 此时 totalElements 同为 0、分页器本就没有可翻的页，两种含义在表现上一致，无需区分
     * （max-offset 配错时服务端会兜回默认值并打 WARN，不会把 0 传到这里）。
     *
     * 可查看条数一律用同一次响应里的 size 换算，不能用本地 pageSize：切换每页条数时本地值已经变了、
     * 而 data 仍是上一次（旧 size）的结果，两者相乘会在新数据回来前算出一个偏大的条数。
     */
    const totalElements = data?.portalOrders?.totalElements ?? 0;
    const accessiblePages = data?.portalOrders?.accessiblePages ?? 0;
    const accessibleElements = accessiblePages * (data?.portalOrders?.size ?? pageSize);
    const isPageCapped = accessiblePages > 0 && totalElements > accessibleElements;
    /* 分页器的 total 用截断值，页码列表与快速跳页自然都翻不过上限；文案里仍显示真实总数 */
    const paginationTotal = isPageCapped ? accessibleElements : totalElements;
    /* 合计文案里的截断提示；未截断时为 null。工具条与分页器 showTotal 两处共用 */
    const viewableCap = isPageCapped ? accessibleElements : null;
    /*
     * 当前条件下真正的最后一页（页码从 1 起算）。取 1 兜底是为了覆盖「结果为空」：
     * 此时服务端下发 accessiblePages=0，而页码没有 0 页，越界只能拉回第 1 页。
     */
    const lastPage = Math.max(1, accessiblePages);
    /*
     * 是否已拿到本次条件下的响应。不能只看 accessiblePages > 0：结果为空时它就是 0，
     * 与「还没拿到数据」无从区分，而这两种情况下的越界纠偏行为不同（前者要拉回，后者要等）。
     */
    const hasResponse = data !== undefined;

    /*
     * 跨页勾选态。只在有导出权限时才渲染 checkbox 列与工具条——选择态只服务于导出，
     * 没有该权限的账号勾了也用不上，白占一列。
     */
    const { permissions } = usePermission();
    const canExport = permissions.has(EXPORT_PERMISSION);
    const selection = useOrderSelection();

    /*
     * 会话缓存里的页码可能已经越界：既可能是超出深翻页上限（翻到第 480 页后把每页条数改大），
     * 也可能是数据量本身变少（上次会话翻到深页，本次同一条件命中的订单已经没那么多）。
     * 两种情况服务端都会截断到它认为的最后一页并返回数据，而本地 page 仍是越界值。
     *
     * 这里不能只处理前一种。antd 受控分页器对越界的 current 只做「显示层夹取」
     * （rc-pagination 的 postState 把 current 夹进 [1, allPages]），并不会回调 onChange 修正
     * 父组件状态，所以本地 page 不拉回的话，每次进页面都会拿越界页码去请求：
     * 数据量少于上限时服务端截断到的那一页早已没有数据，用户看到的是「末页高亮、列表空白」，
     * 且刷新、从详情页返回都不会自愈，只能手动点页码脱困。
     *
     * 判据只用 accessiblePages（经 lastPage 兜底）：服务端下发的就是 min(真实总页数, 深翻页上限页数)，
     * 即「真正的最后一页」，三类越界（超上限 / 数据变少 / 结果为空）它都能正确收敛。
     * 不要改用服务端截断后回传的 page——那是它按深翻页上限算出来的页码，数据量少时仍然越界。
     *
     * 结果为空这一支不能漏：它在页面上看不出异常（分页器 total=0，只显示第 1 页），
     * 但越界的 page 会被 saveQueryState 写回会话缓存长期残留，之后每次进页面都拿它去请求。
     *
     * 必须等首次响应到达、且不在 loading 中再判断：请求进行中时 data 还是上一次的结果，
     * 拿旧的可翻页数去比当前 page 会误伤——例如把每页条数从 100 调小到 20，
     * antd 会顺势把页码推大，而 accessiblePages 还是旧值。
     */
    useEffect(() => {
        if (!loading && hasResponse && page > lastPage) {
            setPage(lastPage);
        }
    }, [loading, hasResponse, lastPage, page]);

    const handleSearch = () => {
        /* 日期区间不合规时直接提示并中止，不把残缺条件发给服务端 */
        const dateRangeError = validateDateRange(filters.dateRange);
        if (dateRangeError) {
            message.warning(t(dateRangeError));
            return;
        }

        setPage(1);
        setAppliedFilters(filters);
        /*
         * 条件变了，跨页累积的勾选已经没有参照系：那些订单可能压根不在新结果集里，
         * 而「已选 N 条」的计数还在，客户点导出会拿到一份与眼前列表无关的文件。
         */
        selection.clear();

        /*
         * 用户点了「搜索」就应该拿到最新数据，所以无条件走网络，并显式带上本次的 variables。
         *
         * 不能依赖「setAppliedFilters 改变 variables 从而触发请求」：
         * 一是条件没变时 variables 深比较相等，Apollo 压根不会重新发起请求；
         * 二是条件变回本次会话搜过的旧值、或从非首页回到第一页时，variables 命中历史缓存，
         * 默认 cache-first 下同样静默复用旧结果。两种情况用户都会拿到一份陈旧数据。
         *
         * 这里传的 page 固定为 0：与上面的 setPage(1) 对应（下发给服务端的页码是 0-based）。
         */
        refetch({ filter: buildQueryFilter(filters), page: 0, size: pageSize });
    };

    const handleReset = () => {
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setPage(1);
        selection.clear();
    };

    const patch = (p: Partial<OrderFilters>) => setFilters((f) => ({ ...f, ...p }));

    const columns = [
        {
            title: t('orderQuery.columns.trackingNumber'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            render: (val: string, record: PortalOrderSummary) => (
                <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => navigate(`/order-detail/${encodeURIComponent(record.trackingNumber)}`)}
                >
                    {val}
                </Button>
            ),
        },
        {
            title: t('orderQuery.columns.channelOrderNumber'),
            dataIndex: 'channelOrderNumber',
            key: 'channelOrderNumber',
            render: (val: string | null) => val || '-',
        },
        {
            title: t('orderQuery.columns.externalTrackingNumber'),
            dataIndex: 'externalTrackingNumber',
            key: 'externalTrackingNumber',
            render: (val: string | null) => val || '-',
        },
        {
            title: t('orderQuery.columns.orderTime'),
            dataIndex: 'orderTime',
            key: 'orderTime',
            render: (val: string | null) => formatUtcToLocal(val),
        },
        {
            title: t('orderQuery.columns.status'),
            dataIndex: 'externalStatus',
            key: 'externalStatus',
            // 后端无法归并状态时返回 null，此时不套 Tag、直接展示 '-'，与其它无值列的占位一致
            render: (val: string | null) => (val ? <Tag>{getExternalStatusLabel(val, t)}</Tag> : '-'),
        },
        {
            /*
             * 外部口岸展示名是专有名词（LAX/NYC 这类口岸码），后端已完成外部/内部映射，
             * 前端不持有任何内部 hubId，也不做 i18n，服务端返回什么就展示什么。
             * 订单尚未揽收、或其口岸未登记在映射配置中时后端返回 null，统一展示 '-'——
             * 这两种情况对客户没有区分价值，后者由服务端记 WARN 提示补配置。
             */
            title: t('orderQuery.columns.pickupHub'),
            dataIndex: 'pickupHubName',
            key: 'pickupHubName',
            render: (val: string | null) => val || '-',
        },
        {
            title: t('orderQuery.columns.pickupAt'),
            dataIndex: 'pickupAt',
            key: 'pickupAt',
            render: (val: string | null) => formatUtcToLocal(val),
        },
        {
            /* 目的地口岸：取 sortingCode（如 "EWR-PHL-A13"）按 '-' 分割后的第一段展示，与包裹列表页第一分拣码逻辑一致 */
            title: t('orderQuery.columns.destinationHub'),
            dataIndex: 'sortingCode',
            key: 'sortingCode',
            render: (val: string | null) => val?.split('-')[0] || '-',
        },
        {
            title: t('orderQuery.columns.arrivedDestinationHubAt'),
            dataIndex: 'destHubArrivedAt',
            key: 'destHubArrivedAt',
            render: (val: string | null) => formatUtcToLocal(val),
        },
    ];

    /* 状态下拉：优先用后端下发的外部状态 code，缺省时回退到本地常量；label 走前端 i18n */
    const statusCodes: readonly string[] =
        statusData?.portalExternalStatuses && statusData.portalExternalStatuses.length > 0
            ? statusData.portalExternalStatuses.map((s) => s.code)
            : EXTERNAL_ORDER_STATUS_CODES;
    const statusOptions = statusCodes.map((code) => ({
        value: code,
        label: getExternalStatusLabel(code, t),
    }));

    const hubOptions = (hubData?.portalPickupHubOptions || [])
        .filter((h) => h.externalPortCode)
        .map((h) => ({
            value: h.externalPortCode as string,
            label: h.externalPortName || (h.externalPortCode as string),
        }));
    /*
     * 下拉无选项时禁用该筛选项；但若已有选中值（会话缓存恢复的条件 + 后端口岸映射此刻不可用），
     * 必须保持可操作，否则客户既筛不到数据、又清不掉这个条件，只能整体「重置」。
     */
    const hubDisabled = hubOptions.length === 0 && filters.pickupHubCodes.length === 0;

    return (
        <div className="bg-white rounded-lg p-6">
            {/*
              * 筛选区用 auto-fill 网格铺开，各控件宽度一律 100%（即列宽），不再逐个定宽：
              * 列数随可用宽度自动增减，窄屏换行、宽屏把富余宽度平分给各列，订单号也就显示得更完整。
              */}
            <div
                className="grid gap-4"
                style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${FILTER_MIN_COLUMN_WIDTH}px, 1fr))` }}
            >
                {/*
                  * 跟踪号跨满整行（模仿 CMS 包裹列表页）：它是唯一支持多值的筛选项，与其余单值字段并排挤在
                  * 一列里的话，粘进来的多个运单号只能在小窗口里盲滑，多值能力等于给了却没法用。
                  * 跨满整行同时让它的右边缘落在网格右边缘上——宽屏排得下 5 列时，那正是「揽收口岸」的右边缘。
                  * label 仍沿用 Field 的"在上"排布，不照搬 CMS 的 label 在左——只为这一个字段换对齐方式，
                  * 会和其余 5 个筛选项割裂，而想要的视觉区分靠"独占一行 + 更宽"已经拿到了。
                  */}
                <div style={{ gridColumn: '1 / -1' }}>
                    <Field label={t('orderQuery.filters.trackingNumber')}>
                        {/* 用文本域承载换行粘贴；原始输入直接下推，由后端按分隔符拆分与去重 */}
                        <Input.TextArea
                            style={{ width: '100%' }}
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            placeholder={t('orderQuery.filters.trackingNumberPlaceholder') as string}
                            value={filters.trackingNumber}
                            allowClear
                            onChange={(e) => patch({ trackingNumber: e.target.value })}
                        />
                    </Field>
                </div>
                {/* 其余筛选项（后端 PortalOrderFilter 支持的字段） */}
                <Field label={t('orderQuery.filters.channelOrderNumber')}>
                    {/* title 兜底：列宽不足以显示完整单号时，鼠标悬停仍能看到已输入的完整值 */}
                    <Input
                        style={{ width: '100%' }}
                        title={filters.channelOrderNumber}
                        placeholder={t('orderQuery.filters.channelOrderNumberPlaceholder') as string}
                        value={filters.channelOrderNumber}
                        allowClear
                        onChange={(e) => patch({ channelOrderNumber: e.target.value })}
                    />
                </Field>
                <Field label={t('orderQuery.filters.externalTrackingNumber')}>
                    <Input
                        style={{ width: '100%' }}
                        title={filters.externalTrackingNumber}
                        placeholder={t('orderQuery.filters.externalTrackingNumberPlaceholder') as string}
                        value={filters.externalTrackingNumber}
                        allowClear
                        onChange={(e) => patch({ externalTrackingNumber: e.target.value })}
                    />
                </Field>
                <Field label={t('orderQuery.filters.status')}>
                    <Select
                        mode="multiple"
                        allowClear
                        maxTagCount="responsive"
                        style={{ width: '100%' }}
                        placeholder={t('orderQuery.filters.statusAll')}
                        value={filters.statuses}
                        options={statusOptions}
                        onChange={(v) => patch({ statuses: v })}
                    />
                </Field>
                {/*
                  * 下单时间跨两列：加上「时:分」后两端各要显示 16 个字符（2026-08-25 00:00），
                  * 连同中间的箭头与右侧日历图标，一列的 280px 下限装不下，末尾的分钟会被截断。
                  * 只把这一项放宽而不是抬高 FILTER_MIN_COLUMN_WIDTH——抬下限会让每一列都变宽，
                  * 1280px 视口下列数从 3 掉到 2，其余筛选项跟着换行，代价远大于这里的收益。
                  *
                  * 跨列用断点类而不是无条件 span 2：网格是 auto-fill，视口窄到只排得下一列时，
                  * span 2 会让浏览器补一列 auto 宽的隐式列，反而把筛选区撑出横向滚动条。
                  * lg（≥1024px）是「两级侧边栏之外仍有 560px 以上可用宽度」的分界，此时必有 2 列。
                  */}
                <div className="col-span-1 lg:col-span-2">
                    <Field label={t('orderQuery.filters.dateRange')}>
                        {/*
                          * allowEmpty 两端均为 false：不允许只选一端提交，与服务端「区间必须完整」的校验对齐。
                          * 不给秒：秒级筛选对客户没有实际意义，且服务端把两端对齐到分钟边界，放开到秒只会让
                          * 用户以为选中的秒生效了。defaultValue 让只点日期不点时间时落在当天 00:00~23:59。
                          *
                          * placeholder 显式给：antd 的 zh/en/es locale 默认是「开始日期 / 结束日期」，
                          * 与带上时分的输入内容对不上。
                          */}
                        <RangePicker
                            style={{ width: '100%' }}
                            allowEmpty={[false, false]}
                            placeholder={[
                                t('orderQuery.filters.dateRangeStartPlaceholder'),
                                t('orderQuery.filters.dateRangeEndPlaceholder'),
                            ]}
                            showTime={{ format: 'HH:mm', defaultValue: DEFAULT_TIME_OF_DAY }}
                            format="YYYY-MM-DD HH:mm"
                            value={filters.dateRange}
                            onChange={(dates) =>
                                patch({ dateRange: (dates ?? [null, null]) as [Dayjs | null, Dayjs | null] })
                            }
                        />
                    </Field>
                </div>
                <Field label={t('orderQuery.filters.originHub')}>
                    <Select
                        mode="multiple"
                        allowClear
                        maxTagCount="responsive"
                        style={{ width: '100%' }}
                        placeholder={t('orderQuery.filters.hubAll')}
                        value={filters.pickupHubCodes}
                        options={hubOptions}
                        disabled={hubDisabled}
                        onChange={(v) => patch({ pickupHubCodes: v })}
                    />
                </Field>
            </div>
            {/* 按钮独立成行右对齐：跟着筛选项挤在网格里会占掉一整列，窄屏时白白多换一行 */}
            <div className="flex justify-end mt-4">
                <Space>
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        {t('orderQuery.filters.search')}
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        {t('orderQuery.filters.reset')}
                    </Button>
                </Space>
            </div>

            {canExport && (
                <OrderExportToolbar
                    totalElements={totalElements}
                    viewableCap={viewableCap}
                    filter={appliedQueryFilter}
                    selectedTrackingNumbers={selection.selectedKeys}
                    onClearSelection={selection.clear}
                />
            )}

            <Table<PortalOrderSummary>
                className="mt-4"
                columns={columns}
                dataSource={data?.portalOrders?.content}
                loading={loading}
                rowKey="trackingNumber"
                /*
                 * preserveSelectedRowKeys 不是优化而是前提：不开的话 antd 在 dataSource 变化时
                 * 会把不在当前页的 selectedRowKeys 全部丢掉，翻页即清空，跨页勾选根本攒不起来。
                 */
                rowSelection={
                    canExport
                        ? {
                              preserveSelectedRowKeys: true,
                              selectedRowKeys: selection.selectedKeys,
                              onChange: selection.onChange,
                          }
                        : undefined
                }
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: t('orderQuery.empty') }}
                pagination={{
                    current: page,
                    pageSize,
                    total: paginationTotal,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100'],
                    /*
                     * 合计文案只出一处：有导出权限时它在工具条里，这里传 undefined 不再重复渲染；
                     * 没有权限时工具条整块不渲染，必须退回分页器展示，否则客户看不到总数。
                     */
                    showTotal: canExport
                        ? undefined
                        : () => (
                              <OrderTotalLabel
                                  total={totalElements}
                                  viewableCap={viewableCap}
                              />
                          ),
                    onChange: (p, ps) => {
                        setPage(p);
                        setPageSize(ps);
                    },
                }}
            />
        </div>
    );
};

export default OrderQueryPage;
